import { useTranslation } from "react-i18next";
import { BusinessCreditsDTO } from "../../../Common/Types-DTOs/ClientUser";
import { DueDiligenceTier } from "../../../Common/Types-DTOs/DueDiligence";
import { DUE_DILIGENCE_TIER_LEVELS, planNameKey } from "../../../Common/Utils/dueDiligencePlans";
import { DueDiligenceCreditCosts } from "../NewCase/PaymentSection";
import {
  CreditBalanceWrapper,
  CreditBalanceValue,
  CreditBalanceCap,
  CreditBalanceBar,
  CreditBalanceFooter,
  CreditBalanceLink,
} from "./Styles";

interface CreditBalanceProps {
  credits: BusinessCreditsDTO;
  /** Without them there's no telling whether the balance runs short. */
  creditCosts: DueDiligenceCreditCosts | null;
  onChangePlan: () => void;
}

export type CreditBalanceTone = "green" | "amber" | "red";

/**
 * How the balance stands against what the plan's levels cost: red when it
 * can't pay even the cheapest, amber when it can't pay the top one.
 * @param balance - The credits left.
 * @param allowedTiers - The plan's tiers; null for every tier.
 * @param creditCosts - What each tier costs in credits.
 * @returns The tone.
 */
export const creditBalanceTone = (
  balance: number,
  allowedTiers: DueDiligenceTier[] | null | undefined,
  creditCosts: DueDiligenceCreditCosts
): CreditBalanceTone => {
  const tiers = (Object.keys(DUE_DILIGENCE_TIER_LEVELS) as DueDiligenceTier[]).filter(
    (tier) => !allowedTiers || allowedTiers.includes(tier)
  );
  const cheapest = tiers[0];
  const top = tiers[tiers.length - 1];
  if (cheapest && balance < creditCosts[cheapest]) {
    return "red";
  }
  if (top && balance < creditCosts[top]) {
    return "amber";
  }
  return "green";
};

/**
 * The credits left from the DD plan, next to where they're spent: the balance
 * against the month's credits, the plan and when it renews. The bar fills with
 * what's left, in the semaphore's tone. Without a plan, credits the business
 * still has (rewards, a plan it left) show too: they come off what a DD
 * costs in euros.
 */
export const CreditBalance = ({ credits, creditCosts, onChangePlan }: CreditBalanceProps) => {
  const { t, i18n } = useTranslation();
  const plan = credits.dueDiligencePlan;

  const { balance } = credits;

  if (!plan) {
    if (balance <= 0) {
      return null;
    }
    return (
      <CreditBalanceWrapper $tone="neutral">
        <CreditBalanceValue>
          {balance.toLocaleString(i18n.language)}
          <CreditBalanceCap>{t("dueDiligence.casesList.creditBalance.credits", { count: balance })}</CreditBalanceCap>
        </CreditBalanceValue>
        <CreditBalanceFooter>{t("dueDiligence.casesList.creditBalance.noSubscription")}</CreditBalanceFooter>
      </CreditBalanceWrapper>
    );
  }

  const tone = creditCosts ? creditBalanceTone(balance, plan.allowedTiers, creditCosts) : "green";
  const renewalDate = new Date(plan.paidUntil).toLocaleDateString(i18n.language, { day: "numeric", month: "short" });

  return (
    <CreditBalanceWrapper $tone={tone}>
      <CreditBalanceValue>
        {balance.toLocaleString(i18n.language)}
        <CreditBalanceCap>
          {plan.monthlyCredits !== null
            ? t("dueDiligence.casesList.creditBalance.ofMonthly", { count: plan.monthlyCredits })
            : t("dueDiligence.casesList.creditBalance.credits", { count: balance })}
        </CreditBalanceCap>
      </CreditBalanceValue>
      {plan.monthlyCredits !== null && plan.monthlyCredits > 0 && (
        <CreditBalanceBar $percent={Math.min(100, (balance / plan.monthlyCredits) * 100)} />
      )}
      <CreditBalanceFooter>
        {tone !== "green" ? (
          <>
            {t("dueDiligence.casesList.creditBalance.renewsOn", { date: renewalDate })}
            {" · "}
            <CreditBalanceLink onClick={onChangePlan}>{t("dueDiligence.casesList.creditBalance.changePlan")}</CreditBalanceLink>
          </>
        ) : (
          t("dueDiligence.casesList.creditBalance.renews", { plan: t(planNameKey(plan.lookupKey)), date: renewalDate })
        )}
      </CreditBalanceFooter>
    </CreditBalanceWrapper>
  );
};
